import { ApiError } from './ApiError.ts';

export type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  /** Bytes sent as-is (file uploads) instead of a JSON body. */
  raw?: Blob;
  signal?: AbortSignal;
  /** Extra non-2xx statuses whose JSON body is returned instead of thrown. */
  okStatuses?: number[];
}

type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

interface ErrorBody {
  error?: { code?: string; message?: string; details?: unknown; requestId?: string };
}

/**
 * The only place the UI talks HTTP. Resource classes (e.g. HealthApi) build on
 * it; components never call fetch directly (enforced by ESLint).
 *
 * - Same-origin cookies (session auth), JSON in and out.
 * - Every failure becomes an ApiError with a stable shape.
 */
export class HttpClient {
  readonly #baseUrl: string;
  readonly #fetch: FetchLike;
  readonly #clientId: string | null;
  /** Called on any 401, e.g. to send an expired session back to the login page. */
  onUnauthorized: (() => void) | null = null;
  /** Called when an organization says this person's access is paused (403 access_paused). */
  onAccessPaused: (() => void) | null = null;

  constructor({
    baseUrl = '/api/v1',
    fetchImpl,
    clientId,
  }: {
    baseUrl?: string;
    fetchImpl?: FetchLike;
    /** This tab's id, sent with writes so live updates can skip their own echo. */
    clientId?: string;
  } = {}) {
    this.#clientId = clientId ?? null;
    this.#baseUrl = baseUrl.replace(/\/$/, '');
    this.#fetch = fetchImpl ?? ((input, init) => globalThis.fetch(input, init));
  }

  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, options);
  }

  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, { ...options, body });
  }

  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, { ...options, body });
  }

  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, { ...options, body });
  }

  /** An absolute API URL (for links the browser follows itself, like downloads). */
  url(path: string): string {
    return this.#baseUrl + path;
  }

  /** A file's bytes (images the page draws itself); failures are ApiErrors like any other. */
  blob(path: string): Promise<Blob> {
    return this.#bytes(path, {});
  }

  /** The first `bytes` bytes of a file (a preview of a long text). */
  blobStart(path: string, bytes: number): Promise<Blob> {
    return this.#bytes(path, { Range: `bytes=0-${bytes - 1}` });
  }

  async #bytes(path: string, headers: Record<string, string>): Promise<Blob> {
    let response: Response;
    try {
      response = await this.#fetch(this.#baseUrl + path, {
        method: 'GET',
        credentials: 'same-origin',
        headers,
      });
    } catch {
      throw new ApiError({
        status: 0,
        code: 'network_error',
        message: 'Could not reach the server',
      });
    }
    if (!response.ok) {
      if (response.status === 401) this.onUnauthorized?.();
      throw new ApiError({
        status: response.status,
        code: 'http_error',
        message: `Request failed (${response.status})`,
      });
    }
    return response.blob();
  }

  /**
   * PUT bytes to an upload link (this API, or file storage itself), used as
   * given, with no cookies: the link is the permission. Returns the ETag
   * storage answered, which confirming needs.
   */
  async sendBytes(link: string, bytes: Blob): Promise<string> {
    let response: Response;
    try {
      response = await this.#fetch(link, { method: 'PUT', body: bytes, credentials: 'omit' });
    } catch {
      throw new ApiError({
        status: 0,
        code: 'network_error',
        message: 'Could not reach the server',
      });
    }
    if (response.ok) {
      const etag = response.headers.get('ETag');
      if (etag === null)
        throw new ApiError({
          status: response.status,
          code: 'upload_failed',
          message: 'The upload was stored but not acknowledged. Try again.',
        });
      return etag;
    }
    const error = ((await readJson(response)) as ErrorBody | null)?.error;
    throw new ApiError({
      status: response.status,
      code: error?.code ?? 'upload_failed',
      message: error?.message ?? `The upload failed (${response.status}). Try again.`,
    });
  }

  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, options);
  }

  async request<T>(
    method: string,
    path: string,
    { query, body, raw, signal, okStatuses = [] }: RequestOptions = {},
  ): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (raw) headers['Content-Type'] = 'application/octet-stream';
    else if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (this.#clientId && method !== 'GET') headers['X-Client-Id'] = this.#clientId;

    let response: Response;
    try {
      response = await this.#fetch(this.#baseUrl + path + toQueryString(query), {
        method,
        headers,
        body: raw ?? (body === undefined ? null : JSON.stringify(body)),
        credentials: 'same-origin',
        signal: signal ?? null,
      });
    } catch (err) {
      if ((err as { name?: string } | null)?.name === 'AbortError') throw err;
      throw new ApiError({
        status: 0,
        code: 'network_error',
        message: 'Could not reach the server',
      });
    }

    const data = await readJson(response);
    if (response.ok || okStatuses.includes(response.status)) return data as T;

    if (response.status === 401) this.onUnauthorized?.();
    const error = (data as ErrorBody | null)?.error ?? {};
    if (error.code === 'access_paused') this.onAccessPaused?.();
    throw new ApiError({
      status: response.status,
      code: error.code ?? 'http_error',
      message: error.message ?? `Request failed (${response.status})`,
      details: error.details,
      requestId: error.requestId ?? response.headers.get('X-Request-Id') ?? undefined,
      body: data,
    });
  }
}

function toQueryString(query: Query | undefined): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) params.append(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

async function readJson(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  if (!(response.headers.get('Content-Type') ?? '').includes('application/json')) return null;
  try {
    return await response.json();
  } catch {
    return null;
  }
}
